import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    status: 'active',
    assigned_agents: []
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCampaigns();
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .eq('role', 'agent');
    setAgents(data || []);
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const campaignsWithDetails = await Promise.all(
        (data || []).map(async (campaign) => {
          const { count } = await supabase
            .from('leads')
            .select('*', { count: 'exact', head: true })
            .eq('campaign_id', campaign.id);
          
          const { data: agentLinks } = await supabase
            .from('campaign_agents')
            .select('agent_id')
            .eq('campaign_id', campaign.id);
          
          let assignedAgents = [];
          if (agentLinks && agentLinks.length > 0) {
            const agentIds = agentLinks.map(a => a.agent_id);
            const { data: agentsData } = await supabase
              .from('profiles')
              .select('id, email, full_name')
              .in('id', agentIds);
            assignedAgents = agentsData || [];
          }
          
          return {
            ...campaign,
            leads_count: count || 0,
            assigned_agents: assignedAgents
          };
        })
      );
      
      setCampaigns(campaignsWithDetails);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data: campaign, error } = await supabase
        .from('campaigns')
        .insert([{
          name: newCampaign.name,
          description: newCampaign.description,
          status: newCampaign.status,
          created_by: user?.id
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      if (newCampaign.assigned_agents.length > 0) {
        const assignments = newCampaign.assigned_agents.map(agentId => ({
          campaign_id: campaign.id,
          agent_id: agentId
        }));
        await supabase.from('campaign_agents').insert(assignments);
      }
      
      resetForm();
      fetchCampaigns();
      alert('Campagne créée avec succès!');
    } catch (error) {
      alert('Erreur: ' + error.message);
    }
  };

  const handleUpdateCampaign = async () => {
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({
          name: newCampaign.name,
          description: newCampaign.description,
          status: newCampaign.status
        })
        .eq('id', editingCampaign.id);
      
      if (error) throw error;
      
      await supabase
        .from('campaign_agents')
        .delete()
        .eq('campaign_id', editingCampaign.id);
      
      if (newCampaign.assigned_agents.length > 0) {
        const assignments = newCampaign.assigned_agents.map(agentId => ({
          campaign_id: editingCampaign.id,
          agent_id: agentId
        }));
        await supabase.from('campaign_agents').insert(assignments);
      }
      
      resetForm();
      fetchCampaigns();
      alert('Campagne mise à jour avec succès!');
    } catch (error) {
      alert('Erreur: ' + error.message);
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!confirm('⚠️ Supprimer définitivement cette campagne ?\n\nCette action supprimera :\n- La campagne\n- Tous les leads associés\n- Tous les fichiers uploadés\n- Les assignations aux agents\n\nCette action est irréversible.')) return;

    try {
      setLoading(true);

      // 1. Récupérer tous les fichiers liés à cette campagne
      const { data: files } = await supabase
        .from('campaign_files')
        .select('id, storage_path')
        .eq('campaign_id', id);

      // 2. Supprimer les fichiers du storage
      for (const file of files || []) {
        if (file.storage_path) {
          await supabase.storage
            .from('campaign-files')
            .remove([file.storage_path])
            .catch(err => console.log('Erreur suppression storage ignorée:', err));
        }
      }

      // 3. Supprimer les fichiers de la table campaign_files
      if (files && files.length > 0) {
        await supabase
          .from('campaign_files')
          .delete()
          .eq('campaign_id', id);
      }

      // 4. Supprimer les leads associés à cette campagne
      await supabase
        .from('leads')
        .delete()
        .eq('campaign_id', id);

      // 5. Supprimer les assignations aux agents
      await supabase
        .from('campaign_agents')
        .delete()
        .eq('campaign_id', id);

      // 6. Enfin, supprimer la campagne elle-même
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // 7. Rafraîchir la liste
      await fetchCampaigns();
      alert('✅ Campagne et toutes ses données associées supprimées avec succès.');

    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression : ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateCampaign = async (campaign) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data: newCamp, error } = await supabase
        .from('campaigns')
        .insert([{
          name: `${campaign.name} (copie)`,
          description: campaign.description,
          status: 'active',
          created_by: user?.id
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      if (campaign.assigned_agents.length > 0) {
        const assignments = campaign.assigned_agents.map(agent => ({
          campaign_id: newCamp.id,
          agent_id: agent.id
        }));
        await supabase.from('campaign_agents').insert(assignments);
      }
      
      fetchCampaigns();
      alert('Campagne dupliquée avec succès!');
    } catch (error) {
      alert('Erreur: ' + error.message);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCampaign(null);
    setNewCampaign({ name: '', description: '', status: 'active', assigned_agents: [] });
  };

  const startEdit = (campaign) => {
    setEditingCampaign(campaign);
    setNewCampaign({
      name: campaign.name,
      description: campaign.description || '',
      status: campaign.status,
      assigned_agents: campaign.assigned_agents.map(a => a.id)
    });
    setShowForm(true);
  };

  const getStatusStyle = (status) => {
    switch(status) {
      case 'active': return { bg: '#dcfce7', color: '#166534', text: 'Actif' };
      case 'paused': return { bg: '#fff3cd', color: '#856404', text: 'En pause' };
      case 'completed': return { bg: '#e2e3e5', color: '#383d41', text: 'Terminé' };
      default: return { bg: '#f3f4f6', color: '#1f2937', text: status };
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Chargement...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827' }}>Campagnes</h1>
          <button
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
            style={{
              backgroundColor: showForm ? '#6b7280' : '#2563eb',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            {showForm ? 'Annuler' : '+ Nouvelle campagne'}
          </button>
        </div>

        {showForm && (
          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '0.5rem', marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
              {editingCampaign ? 'Modifier' : 'Nouvelle'} campagne
            </h2>
            
            <input
              type="text"
              placeholder="Nom de la campagne"
              value={newCampaign.name}
              onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
              style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
            />
            
            <textarea
              placeholder="Description"
              value={newCampaign.description}
              onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
              style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
              rows="3"
            />
            
            <select
              value={newCampaign.status}
              onChange={(e) => setNewCampaign({...newCampaign, status: e.target.value})}
              style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
            >
              <option value="active">Actif</option>
              <option value="paused">En pause</option>
              <option value="completed">Terminé</option>
            </select>
            
            <select
              multiple
              value={newCampaign.assigned_agents}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                setNewCampaign({...newCampaign, assigned_agents: selected});
              }}
              style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', minHeight: '100px' }}
            >
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>{agent.full_name || agent.email}</option>
              ))}
            </select>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={editingCampaign ? handleUpdateCampaign : handleCreateCampaign}
                style={{ backgroundColor: '#059669', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer' }}
              >
                {editingCampaign ? 'Mettre à jour' : 'Créer'}
              </button>
              <button
                onClick={resetForm}
                style={{ backgroundColor: '#6b7280', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer' }}
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gap: '1rem' }}>
          {campaigns.map(campaign => {
            const status = getStatusStyle(campaign.status);
            return (
              <div key={campaign.id} style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600' }}>{campaign.name}</h3>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    backgroundColor: status.bg,
                    color: status.color
                  }}>
                    {status.text}
                  </span>
                </div>
                
                {campaign.description && (
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>{campaign.description}</p>
                )}
                
                <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem', color: '#4b5563', fontSize: '0.875rem' }}>
                  <span>📊 {campaign.leads_count} leads</span>
                  <span>👥 {campaign.assigned_agents.length} agents</span>
                  <span>📅 {new Date(campaign.created_at).toLocaleDateString()}</span>
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                  <button
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                    style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer' }}
                  >
                    Voir détails
                  </button>
                  <button
                    onClick={() => navigate(`/campaigns/${campaign.id}/files`)}
                    style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer' }}
                  >
                    📁 Fichiers
                  </button>
                  <button
                    onClick={() => startEdit(campaign)}
                    style={{ backgroundColor: '#f59e0b', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer' }}
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDuplicateCampaign(campaign)}
                    style={{ backgroundColor: '#8b5cf6', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer' }}
                  >
                    Dupliquer
                  </button>
                  <button
                    onClick={() => handleDeleteCampaign(campaign.id)}
                    style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer' }}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CampaignsPage;
